import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulbOn } from '@fortawesome/pro-light-svg-icons/faLightbulbOn';

import React, { useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { Text, TextType } from 'ui-components/components/text';

import { SellerSettings } from '../../../../../enums/seller-settings/seller-settings';
import { useEnableNewHtmlEditorForPackingSlips } from '../../../../../feature-flags/hooks/use-enable-new-html-editor-for-packing-slips';
import { useEnableQrCodesInHtmlPackingSlips } from '../../../../../feature-flags/hooks/use-enable-qr-codes-in-html-packing-slips';
import { useEnableNewLayoutOptions } from '../../../../../feature-flags/hooks/use-enable-new-layout-options';
import {
  useInventoryTrackingEnabled,
  useLabelOutput,
  usePackingSlipBarcode,
  usePackingSlipFormat,
  usePackingSlipInventoryLocation,
  usePackingSlipLogos,
  usePackingSlipPrices,
  usePackingSlipShowDiscounts,
  usePackingSlipSkus,
  usePackingSlipSorting,
  usePackingSlipZeroQuantity,
  usePackingSlipReturnQrCode,
  useIsShipstationReturnsEnabled,
} from '../../../../../hooks/use-seller-setting';

import { FeatureGatedPromotionalBanner } from '../../../../../feature-gates/feature-gated-promotional-banner';
import { usePackingSlipsCustomizedFeatureGate } from '../../../../../feature-gates/use-packing-slips-customized-feature-gate';
import { findMatchingSettingEnum } from '../helpers';
import { PackingSlipSettingWithTooltip } from './packing-slip-setting-with-tooltip';

import {
  defaultPackingSlipFormats,
  INCLUDES_PACKING_SLIP_SUFFIX,
  newPackingSlipFormats,
  newPackingSlipFormatsFromLabel,
  packingSlipFormatsFromLabel,
  packingSlipSortings,
} from './constants';

import sharedMessages from '../shared-messages';
import style from '../style.module.less';
import messages from './messages';

/**
 * @param {object} props
 * @param {(settingsState: object) => void} props.onInit
 * @param {object} props.settingsState
 * @param {(key: string, value: any) => void} props.onDropdownChange
 * @param {(value: boolean, key: string) => void} props.onRadioCheckListChange
 * @returns {JSX.Element}
 */
export const PackingSlipDocumentOptionsModal = ({ onInit, settingsState = {}, onDropdownChange, onRadioCheckListChange }) => {
  const intl = useIntl();
  const isNewHtmlEditorEnabled = useEnableNewHtmlEditorForPackingSlips();
  const isQrCodesEnabled = useEnableQrCodesInHtmlPackingSlips();
  const isInventoryEnabled = useInventoryTrackingEnabled();
  const labelOutput = useLabelOutput();
  const labelIncludesPackingSlip = labelOutput.includes(INCLUDES_PACKING_SLIP_SUFFIX);
  // TODO: [SPD-19601]: Remove this logic when feature flag is removed
  const enableNewLayoutOptions = useEnableNewLayoutOptions();
  const {
    printerPackingSlipsCustomizedFeature: isPackingSlipsCustomizationEnabled,
    disabledMessage: packingSlipsCustomizationDisabledMessage,
  } = usePackingSlipsCustomizedFeatureGate();
  const createSettingChangeHandler = (key) => (value) => {
    const safeValue = value ?? false;
    onRadioCheckListChange(safeValue, key);
  };

  const packingSlipFormats = useMemo(
    () => (enableNewLayoutOptions ? [...defaultPackingSlipFormats, ...newPackingSlipFormats] : defaultPackingSlipFormats),
    [enableNewLayoutOptions],
  );

  const currentPackingSlipFormatsFromLabel = enableNewLayoutOptions
    ? [...packingSlipFormatsFromLabel, ...newPackingSlipFormatsFromLabel]
    : packingSlipFormatsFromLabel;

  const packingSlipFormat = usePackingSlipFormat();
  const packingSlipSorting = usePackingSlipSorting();
  const packingSlipLogos = usePackingSlipLogos();
  const packingSlipPrices = usePackingSlipPrices();
  const packingSlipSkus = usePackingSlipSkus();
  const packingSlipBarcode = usePackingSlipBarcode();
  const packingSlipInventoryLocation = usePackingSlipInventoryLocation();
  const packingSlipZeroQuantity = usePackingSlipZeroQuantity();
  const packingSlipShowDiscounts = usePackingSlipShowDiscounts();
  const packingSlipReturnQrCode = usePackingSlipReturnQrCode();
  const isReturnsPortalEnabled = useIsShipstationReturnsEnabled();

  const initialSettingsState = useMemo(
    () => ({
      [SellerSettings.packingSlipFormat.key]: packingSlipFormat,
      [SellerSettings.packingSlipSorting.key]: packingSlipSorting,
      [SellerSettings.packingSlipLogos.key]: packingSlipLogos,
      [SellerSettings.packingSlipPrices.key]: packingSlipPrices,
      [SellerSettings.packingSlipSkus.key]: packingSlipSkus,
      [SellerSettings.packingSlipBarcode.key]: packingSlipBarcode,
      [SellerSettings.packingSlipInventoryLocation.key]: packingSlipInventoryLocation,
      [SellerSettings.packingSlipZeroQuantity.key]: packingSlipZeroQuantity,
      [SellerSettings.packingSlipShowDiscounts.key]: packingSlipShowDiscounts,
      [SellerSettings.packingSlipReturnQrCode.key]: packingSlipReturnQrCode,
    }),
    [
      packingSlipFormat,
      packingSlipSorting,
      packingSlipLogos,
      packingSlipPrices,
      packingSlipSkus,
      packingSlipBarcode,
      packingSlipInventoryLocation,
      packingSlipZeroQuantity,
      packingSlipShowDiscounts,
      packingSlipReturnQrCode,
    ],
  );

  useEffect(() => {
    onInit(initialSettingsState);
  }, [onInit, initialSettingsState]);

  const format = settingsState[SellerSettings.packingSlipFormat.key] || initialSettingsState[SellerSettings.packingSlipFormat.key];
  const sortItemsBy = settingsState[SellerSettings.packingSlipSorting.key] || initialSettingsState[SellerSettings.packingSlipSorting.key];
  const includeLogos = settingsState[SellerSettings.packingSlipLogos.key] ?? initialSettingsState[SellerSettings.packingSlipLogos.key];
  const includeItemPrice =
    settingsState[SellerSettings.packingSlipPrices.key] ?? initialSettingsState[SellerSettings.packingSlipPrices.key];
  const includeItemSku = settingsState[SellerSettings.packingSlipSkus.key] ?? initialSettingsState[SellerSettings.packingSlipSkus.key];
  const includeScanBarcode =
    settingsState[SellerSettings.packingSlipBarcode.key] ?? initialSettingsState[SellerSettings.packingSlipBarcode.key];
  const includeItemLocation =
    settingsState[SellerSettings.packingSlipInventoryLocation.key] ?? initialSettingsState[SellerSettings.packingSlipInventoryLocation.key];

  const hideZeroQuantity =
    settingsState[SellerSettings.packingSlipZeroQuantity.key] ?? initialSettingsState[SellerSettings.packingSlipZeroQuantity.key];
  const showDiscounts =
    settingsState[SellerSettings.packingSlipShowDiscounts.key] ?? initialSettingsState[SellerSettings.packingSlipShowDiscounts.key];
  const includeReturnQrCode =
    settingsState[SellerSettings.packingSlipReturnQrCode.key] ?? initialSettingsState[SellerSettings.packingSlipReturnQrCode.key];
  return (
    <>
      <BannerMessageV2
        content={<Text textType={TextType.Caption} value={sharedMessages.affectsAllUsersWarning} />}
        layout={BannerLayout.Inline}
        status={BannerStatus.Info}
      />
      <Text textType={TextType.Description} value={messages.packingSlipOptionsDescription} />
      <div className={style['print-options-grid']}>
        <div>
          <Text textType={TextType.Label} value={messages.packingSlipFormatLabel} />
          <DropdownMenu disabled={labelIncludesPackingSlip} dropdownMenuShouldHaveWidthOfButton fullWidth variant='form'>
            <DropdownMenuToggler
              className={style['dropdown-toggler']}
              title={intl.formatMessage(findMatchingSettingEnum(packingSlipFormats, format).messageDescriptor)}
            >
              {intl.formatMessage(
                labelIncludesPackingSlip
                  ? findMatchingSettingEnum(currentPackingSlipFormatsFromLabel, labelOutput).messageDescriptor
                  : findMatchingSettingEnum(packingSlipFormats, format).messageDescriptor,
              )}
            </DropdownMenuToggler>
            {packingSlipFormats.map(({ messageDescriptor, value }) => (
              <DropdownMenuItem key={value} onClick={() => onDropdownChange(SellerSettings.packingSlipFormat.key, value)}>
                {intl.formatMessage(messageDescriptor)}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
          {labelIncludesPackingSlip && (
            <BannerMessageV2
              className={style['packing-slip-warning']}
              content={<Text textType={TextType.Caption} value={messages.packingSlipOnLabelWarning} />}
              customIcon={<FontAwesomeIcon icon={faLightbulbOn} />}
              layout={BannerLayout.Default}
              status={BannerStatus.Custom}
            />
          )}
        </div>
        <div>
          <Text textType={TextType.Label} value={messages.orderItemsByLabel} />
          <DropdownMenu dropdownMenuShouldHaveWidthOfButton fullWidth id={SellerSettings.packingSlipSorting.key} variant='form'>
            <DropdownMenuToggler
              className={style['dropdown-toggler']}
              title={intl.formatMessage(findMatchingSettingEnum(packingSlipSortings, sortItemsBy).messageDescriptor)}
            >
              {intl.formatMessage(findMatchingSettingEnum(packingSlipSortings, sortItemsBy).messageDescriptor)}
            </DropdownMenuToggler>
            {packingSlipSortings.map(({ messageDescriptor, value }) => (
              <DropdownMenuItem key={value} onClick={() => onDropdownChange(SellerSettings.packingSlipSorting.key, value)}>
                {intl.formatMessage(messageDescriptor)}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
        </div>
        {!isPackingSlipsCustomizationEnabled && (
          <FeatureGatedPromotionalBanner
            className={style['promo-banner']}
            featureName={<FormattedMessage {...messages.packingSlipCustomizationFeatureName} />}
          />
        )}
        <PackingSlipSettingWithTooltip
          disabled={!isPackingSlipsCustomizationEnabled}
          isChecked={includeLogos}
          label={messages.storeLogoOption}
          name={SellerSettings.packingSlipLogos.key}
          onChange={createSettingChangeHandler(SellerSettings.packingSlipLogos.key)}
          title={messages.storeLogoLabel}
          tooltipMessage={packingSlipsCustomizationDisabledMessage}
        />
        <PackingSlipSettingWithTooltip
          disabled={!isPackingSlipsCustomizationEnabled}
          isChecked={includeItemPrice}
          label={messages.itemPriceOption}
          name={SellerSettings.packingSlipPrices.key}
          onChange={createSettingChangeHandler(SellerSettings.packingSlipPrices.key)}
          title={messages.itemPriceLabel}
          tooltipMessage={packingSlipsCustomizationDisabledMessage}
        />
        <PackingSlipSettingWithTooltip
          disabled={!isPackingSlipsCustomizationEnabled}
          isChecked={includeItemSku}
          label={messages.itemSkuOption}
          name={SellerSettings.packingSlipSkus.key}
          onChange={createSettingChangeHandler(SellerSettings.packingSlipSkus.key)}
          title={messages.itemSkuLabel}
          tooltipMessage={packingSlipsCustomizationDisabledMessage}
        />
        <PackingSlipSettingWithTooltip
          disabled={!isPackingSlipsCustomizationEnabled}
          isChecked={includeScanBarcode}
          label={messages.scanToViewOption}
          name={SellerSettings.packingSlipBarcode.key}
          onChange={createSettingChangeHandler(SellerSettings.packingSlipBarcode.key)}
          title={messages.scanToViewLabel}
          tooltipMessage={packingSlipsCustomizationDisabledMessage}
        />
        <PackingSlipSettingWithTooltip
          disabled={!isPackingSlipsCustomizationEnabled || !isInventoryEnabled}
          isChecked={includeItemLocation}
          label={messages.itemLocationOption}
          name={SellerSettings.packingSlipInventoryLocation.key}
          onChange={createSettingChangeHandler(SellerSettings.packingSlipInventoryLocation.key)}
          title={messages.itemLocationLabel}
          tooltipMessage={packingSlipsCustomizationDisabledMessage}
        />
        <PackingSlipSettingWithTooltip
          disabled={!isPackingSlipsCustomizationEnabled}
          isChecked={showDiscounts}
          label={messages.showDiscountsOption}
          name={SellerSettings.packingSlipShowDiscounts.key}
          onChange={createSettingChangeHandler(SellerSettings.packingSlipShowDiscounts.key)}
          title={messages.showDiscountsLabel}
          tooltipMessage={packingSlipsCustomizationDisabledMessage}
        />
        <PackingSlipSettingWithTooltip
          disabled={!isPackingSlipsCustomizationEnabled}
          isChecked={hideZeroQuantity}
          label={messages.packingSlipZeroQuantity}
          name={SellerSettings.packingSlipZeroQuantity.key}
          onChange={createSettingChangeHandler(SellerSettings.packingSlipZeroQuantity.key)}
          title={messages.packingSlipHideZeroQuantityLabel}
          tooltipMessage={packingSlipsCustomizationDisabledMessage}
        />
        {isNewHtmlEditorEnabled && isQrCodesEnabled && (
          <PackingSlipSettingWithTooltip
            disabled={!isPackingSlipsCustomizationEnabled || !isReturnsPortalEnabled}
            isChecked={includeReturnQrCode}
            label={messages.returnQrCodeOption}
            name={SellerSettings.packingSlipReturnQrCode.key}
            onChange={createSettingChangeHandler(SellerSettings.packingSlipReturnQrCode.key)}
            title={messages.returnQrCodeLabel}
            tooltipMessage={!isReturnsPortalEnabled ? messages.returnQrCodeDisabledTooltip : packingSlipsCustomizationDisabledMessage}
          />
        )}
      </div>
    </>
  );
};

export default PackingSlipDocumentOptionsModal;
